import config from '../config';
import Status from '../Data/Status';
import { isResponseLimitReached } from './handleServiceError';
import type { ClosedInformation, ExpirationStatus, Respondent, SurveyResponse } from '../types';

type ResponseBody = Record<string, unknown>;

interface PopupCampaignMemberData {
  response?: Record<string, unknown>;
  custom?: unknown;
  [key: string]: unknown;
}

const baseUrl = config.REACT_APP_API_URL;
const queryParams = new URLSearchParams(globalThis.location.search);
const campaignUniversalLinkId = queryParams.get('campaign');
const campaignId = Number.parseInt(queryParams.get('cid') ?? '', 10);
const campaignMemberPublicGuid = queryParams.get('id');
const sha = queryParams.get('sha');
const email = queryParams.get('email');
const name = queryParams.get('name');
const firstName = queryParams.get('firstName');
const lastName = queryParams.get('lastName');
const ext = queryParams.get('ext');
const employeeEmail = queryParams.get('employee');
const employeeExternalId = queryParams.get('employeeExternalId');
const responseUpdatePath = sha
  ? `/responses/personalized-link/${campaignMemberPublicGuid}`
  : `/responses/${campaignMemberPublicGuid}`;

const reservedQueryParams = new Set([
  'campaign',
  'cid',
  'id',
  'sha',
  'email',
  'name',
  'firstName',
  'lastName',
  'ext',
  'phone',
  'rating',
  'employee',
  'employeeExternalId',
  'unsubscribe',
  'hideHeader',
  'hideFooter',
  'instanceId'
]);

const buildCustom = (): Record<string, string | null> | null => {
  const custom: Record<string, string | null> = {};
  const filteredParams = Array.from(queryParams.keys()).filter((p) => !reservedQueryParams.has(p));
  for (const key of filteredParams) {
    custom[key] = queryParams.get(key);
  }
  return Object.keys(custom).length > 0 ? custom : null;
};

export default class Service {
  proxy: Service;

  private _getResponseDebounce: ReturnType<typeof setTimeout> | null = null;

  private _lastGetResponsePromise: Promise<Respondent> | null = null;

  private _editResponseDebounce: ReturnType<typeof setTimeout> | null = null;

  private _lastEditResponsePromise: Promise<SurveyResponse> | null = null;

  private _addFinalResponseDebounce: ReturnType<typeof setTimeout> | null = null;

  private _lastAddFinalResponsePromise: Promise<SurveyResponse> | null = null;

  constructor() {
    // class constructor code
    this.proxy = new Proxy(this, {
      get: (target, key) => {
        const value = Reflect.get(target, key);
        if (typeof value === 'function') {
          return async (...args: unknown[]) => {
            if (!globalThis.navigator.onLine) {
              // eslint-disable-next-line @typescript-eslint/no-throw-literal
              throw Status.NO_INTERNET;
            }
            return value.apply(target, args);
          };
        }
        return value;
      }
    });
  }

  async addUniquePersonalizedLinkResponse(): Promise<void> {
    const custom = buildCustom();
    const dynamicQueryParams = new Set(['unsubscribe', 'hideHeader', 'hideFooter', 'instanceId']);
    let phoneNumber = '';
    const prefixForHash = globalThis.location.search
      .substring(1)
      .split('&')
      .map((e) => {
        const key = e.split('=')[0];
        if (reservedQueryParams.has(key) && !dynamicQueryParams.has(key)) {
          const value = e.split('=')[1];
          if (key === 'phone') phoneNumber = decodeURI(value);
          if (key === 'sha' || key === 'rating') return '';
          return decodeURI(value);
        }
        return '';
      })
      .join('');

    const respondent = [
      {
        respondent: {
          emailAddress: email ?? `${campaignMemberPublicGuid}@nps.today`,
          firstName: firstName ?? name ?? '',
          lastName: lastName ?? '',
          phoneNumber: phoneNumber ?? '',
          externalId: ext === null || ext === '' ? '' : ext
        },
        employee: { email: employeeEmail },
        guid: campaignMemberPublicGuid,
        custom
      }
    ];
    const header = createHeader('post', respondent);
    await callEndpoint(
      `/campaigns/organization/${campaignId}?prefixAndSha=${encodeURIComponent(
        `${prefixForHash}|${sha}`
      )}`,
      header
    );
  }

  async addUniversalLinkResponse(response: ResponseBody): Promise<string> {
    const custom = buildCustom();
    response.custom = custom;
    response.firstName = firstName ?? response.firstName;
    response.lastName = lastName ?? response.lastName;
    response.email = email ?? response.email;
    response.phoneNumber = queryParams.get('phone') ?? response.phoneNumber;
    response.employeeEmail = employeeEmail;
    response.employeeExternalId = employeeExternalId;
    const header = createHeader('post', response);
    const postResponse = await callEndpoint(
      `/anonymous/${campaignUniversalLinkId}/respondent`,
      header
    );

    const newPublicGuid = await postResponse.json();
    return newPublicGuid;
  }

  async updatePublicConsentOnAnonymousCampaignMember(
    response: ResponseBody,
    publicGuid: string
  ): Promise<void> {
    const header = createHeader('put', response);
    await callEndpoint(`/anonymous/${campaignUniversalLinkId}/respondent/${publicGuid}`, header);
  }

  // creates a response based on data sent to the survey from the popup globalThis. Should only be used if the enablePopup query param is set to true
  async addPopupResponse(
    response: ResponseBody,
    popopCampaignMemberData: PopupCampaignMemberData = {}
  ): Promise<void> {
    // some data may be relevant to add to the response directly from the popop, fx anon: true
    const responsePopupData = popopCampaignMemberData?.response ?? {};

    // custom data is stored as a stringified json object in the popup, be we should accept JSON or string
    let customPopupData: unknown;
    try {
      customPopupData = JSON.stringify(popopCampaignMemberData.custom);
    } catch (error) {
      console.debug('Failed to stringify popup custom data', error);
      customPopupData = popopCampaignMemberData.custom;
    }

    const popupResponse = {
      ...popopCampaignMemberData,
      custom: customPopupData,
      response: {
        ...response,
        ...responsePopupData
      }
    };

    const header = createHeader('post', popupResponse);
    const campaign = await this.getCampaignInfo();
    await callEndpoint(`/popup/survey/${campaign.publicId}`, header);
  }

  // checks if response has been closed so that it cannot be reopened which would result in unexpected behaviours.
  async responseClosed(): Promise<ClosedInformation | null> {
    const res = await callEndpoint(`/responses/closed?id=${campaignMemberPublicGuid}`);
    if (res.status === 204) return null;
    const json = await res.json();
    return json;
  }

  // updates the response to it's final state. Is used when final submit button is clicked.
  async addFinalResponse(response: ResponseBody): Promise<SurveyResponse> {
    if (this._lastAddFinalResponsePromise) {
      return this._lastAddFinalResponsePromise;
    }

    if (this._addFinalResponseDebounce) {
      clearTimeout(this._addFinalResponseDebounce);
    }

    this._lastAddFinalResponsePromise = new Promise<SurveyResponse>((resolve, reject) => {
      this._addFinalResponseDebounce = setTimeout(async () => {
        try {
          const header = createHeader('put', response);
          const res = await callEndpoint(
            `${responseUpdatePath}?emailAnswer=true&skipAlertQueue=true`,
            header
          );
          const json = await res.json();
          resolve(json);
        } catch (error) {
          reject(error);
        } finally {
          this._lastAddFinalResponsePromise = null;
        }
      }, 300);
    });

    return this._lastAddFinalResponsePromise;
  }

  // gets response information and more (template, campaign info, etc.)
  async getResponse(): Promise<Respondent> {
    if (this._lastGetResponsePromise) {
      return this._lastGetResponsePromise;
    }

    if (this._getResponseDebounce) {
      clearTimeout(this._getResponseDebounce);
    }

    this._lastGetResponsePromise = new Promise<Respondent>((resolve, reject) => {
      this._getResponseDebounce = setTimeout(async () => {
        try {
          const res = await callEndpoint(
            `/responses/${campaignMemberPublicGuid}?mergeTextFields=true`
          );
          const json = await res.json();
          resolve(json);
        } catch (error) {
          reject(error);
        } finally {
          this._lastGetResponsePromise = null;
        }
      }, 300);
    });

    return this._lastGetResponsePromise;
  }

  // updates response. Is used in various events in the survey.
  async editResponse(response: ResponseBody): Promise<SurveyResponse> {
    if (this._lastEditResponsePromise) {
      return this._lastEditResponsePromise;
    }

    if (this._editResponseDebounce) {
      clearTimeout(this._editResponseDebounce);
    }

    this._lastEditResponsePromise = new Promise<SurveyResponse>((resolve, reject) => {
      this._editResponseDebounce = setTimeout(async () => {
        try {
          const header = createHeader('put', response);
          const res = await callEndpoint(`${responseUpdatePath}?emailAnswer=true`, header);

          // 403 is most likely due to BOT answer being previously detected
          // if forbidden error, return the response from params, as fetch returned no response (if not returning, application runs into error)
          if (res?.status === 403) {
            resolve(response);
            return;
          }

          const json = await res.json();
          resolve(json);
        } catch (error) {
          if (isResponseLimitReached(error)) {
            reject(error);
            return;
          }
          resolve(response);
        } finally {
          this._lastEditResponsePromise = null;
        }
      }, 300);
    });

    return this._lastEditResponsePromise;
  }

  // gets campaign info. Is only used in universal links.
  async getCampaignInfo(): Promise<Respondent> {
    const res = await callEndpoint(
      `/anonymous/${campaignUniversalLinkId}${globalThis.location.search}`
    );
    const json = await res.json();
    return json;
  }

  // unsubscribes the respondent from the organization. Is used if the respondent wishes not to get surveys again.
  async unsubscribe(): Promise<void> {
    const header = createHeader('put');
    await callEndpoint(`/respondents/${campaignMemberPublicGuid}/unsubscribe`, header);
  }

  async evaluateSurveyExpirationStatus(): Promise<ExpirationStatus> {
    const header = createHeader('put');
    const res = await callEndpoint(
      `/v2/campaignMembers/expiration-status/${campaignMemberPublicGuid}`,
      header
    );
    const json = await res.json();
    return json;
  }
}

// helper function for creating header
let turnstileToken: string | undefined;
export const handleTurnstileToken = (token: string) => {
  turnstileToken = token;
};

function createHeader(method: string, body?: unknown): RequestInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (turnstileToken) {
    headers['turnstile-token'] = turnstileToken;
  }

  return {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers: {
      ...headers
    }
  };
}

class EndpointError extends Error {
  status: number;

  errorDetails: string;

  constructor(status: number, errorDetails: string) {
    super(errorDetails);
    this.status = status;
    this.errorDetails = errorDetails;
  }
}

// helper function for catching failues when the server doesn't throw an error but the request is not successful
const callEndpoint = async (endpoint: string, header?: RequestInit): Promise<Response> => {
  const response = await fetch(baseUrl + endpoint, header);

  const newTurnstileToken = response.headers.get('turnstile-token');
  if (newTurnstileToken) {
    handleTurnstileToken(newTurnstileToken);
  }

  if (!response.ok) {
    const json = await response
      .json()
      .then((j) => {
        return j;
      })
      .catch((error) => {
        // silently catch any serialization errors
        // avoids ending up with an unknown error before getting a chance to inspect the status of the response
        console.debug('Failed to parse error response', error);
        return undefined;
      });
    const errorList = Object.values(json?.errors ?? '').join('\n');

    // Handle Turnstile token error
    if (response.status === 401 && json?.message?.includes('turnstile')) {
      throw new EndpointError(Status.TURNSTILE, json?.message);
    }

    if (response.status === 403 && json?.type === 'Forbidden.ResponseLimitReached') {
      throw new EndpointError(Status.RESPONSE_LIMIT_REACHED, json?.detail ?? '');
    }

    if (response.status === 400) {
      throw new EndpointError(Status.BAD_REQUEST, errorList);
    }
    throw new EndpointError(Status.UNKNOWN, errorList);
  }
  return response;
};
